import { HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";

export default function Giving() {
  return (
    <div className="py-16 md:py-24">
      <SEO
        title="Community Giving | Tallahassee Lassie Pet Care"
        description="Tallahassee Lassie Pet Care gives back to the Tallahassee community every month by supporting local nonprofits, animal rescues, and community organizations."
        path="/giving"
      />
      <div className="container mx-auto px-4 max-w-5xl">
        <Reveal>
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 text-primary rounded-full mb-6">
              <HeartHandshake size={40} />
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-foreground mb-6">Giving Back</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              At Tallahassee Lassie, we believe a business should be deeply rooted in its community. 
              We are proud to partner with local organizations that make Tallahassee a better place.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div className="bg-muted/50 p-10 border border-border/50 mb-16" style={{ borderRadius: "24px 0 24px 0" }}>
            <h2 className="text-2xl font-serif font-bold mb-3">Community Giving</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Tallahassee Lassie Pet Care is happy to give back to our community
              on a monthly basis. To date, we have contributed to the following
              local nonprofits:
            </p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mt-6">
              {[
                "Animal Shelter Foundation",
                "Big Bend Hospice",
                "CARE Animal Rescue",
                "Equality Florida",
                "Independence Landing",
                "Fat Cat Cafe",
                "Leon County Humane Society",
                "NAMI Tallahassee",
                "North Florida Wildlife Center",
                "Pug Rescue of Florida",
                "Oasis Center for Women and Girls",
                "Refuge House, Inc.",
                "St. Francis Wildlife Rescue",
                "Survive and Thrive Advocacy Center",
                "Tally Cat Cafe",
                "United Way of the Big Bend",
              ].map((org, i) => (
                <div key={i} className="flex items-center gap-2 py-1">
                  <HeartHandshake size={14} className="text-primary shrink-0" />
                  <span className="text-foreground/80">{org}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          <Reveal direction="left">
            <div className="bg-secondary/5 p-10 border border-secondary/20 h-full" style={{ borderRadius: "32px 0 32px 0" }}>
              <h2 className="text-3xl font-serif font-bold mb-4 text-secondary">Independence Landing</h2>
              <p className="text-lg text-foreground/80 leading-relaxed mb-6">
                We are incredibly proud to be a services partner with <strong>Independence Landing</strong>, an amazing nonprofit organization in Tallahassee. 
                They provide an independent living community for adults with cognitive and physical disabilities.
              </p>
              <p className="text-lg text-foreground/80 leading-relaxed">
                Through our partnership, we help ensure residents can experience the joy and therapeutic benefits of pet ownership without worrying about the physical demands of daily dog walking.
              </p>
            </div>
          </Reveal>
          <Reveal direction="right">
            <div className="relative h-full min-h-[300px] bg-muted overflow-hidden" style={{ borderRadius: "0 40px 0 40px" }}>
              <img 
                src="https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/ca936253-f0f0-46f7-a23f-80fbb38bc677/George.jpg" 
                alt="A happy dog from our community programs" 
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-primary/20 mix-blend-multiply"></div>
            </div>
          </Reveal>
        </div>

        <Reveal>
          <div className="bg-primary text-white p-12 text-center relative overflow-hidden" style={{ borderRadius: "24px 0 24px 0" }}>
            <div className="absolute top-0 right-0 p-8 opacity-10">
              <HeartHandshake size={120} />
            </div>
            <h2 className="text-3xl font-serif font-bold mb-6 relative z-10">Lend a Leash Program</h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8 relative z-10">
              We also run our own internal community program providing sponsored walks to locals recovering from surgery or medical crises.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 relative z-10">
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 h-14 px-8 text-lg">
                <Link href="/lend-a-leash">
                  Learn About Lend a Leash
                </Link>
              </Button>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
